import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Instagram, Facebook, Mail, Send, Search, Phone } from "lucide-react";
import api, { formatApiError } from "../lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email: email.trim(), source: "footer" });
      setDone(true);
      toast.success("You're in — thanks!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Please try again");
    } finally { setBusy(false); }
  };

  return (
    <footer className="border-t border-zinc-200 bg-white mt-16 sm:mt-24" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 py-12 sm:py-16 grid grid-cols-2 md:grid-cols-5 gap-8 sm:gap-10">
        <div className="col-span-2 md:col-span-2">
          <div className="flex items-center gap-3 sm:gap-4 mb-4 flex-wrap">
            <img src="/brand/fruit-blossoms.png" alt="Fruit Blossoms" className="h-12 sm:h-14 w-auto" />
            <img src="/brand/fruit-popz.png" alt="Fruit Popz" className="h-10 sm:h-12 w-auto" />
          </div>
          <p className="text-sm text-zinc-600 leading-relaxed mb-5">
            Artisan fruit pops from Fruit Blossoms — making fruits bloom, delivered across Kingston & Saint Catherine.
          </p>
          {/* Newsletter signup */}
          <form onSubmit={subscribe} className="max-w-sm" data-testid="newsletter-form">
            <div className="overline text-zinc-500 mb-2">Newsletter</div>
            {done ? (
              <div className="text-sm text-green-700 font-semibold">You're subscribed — watch your inbox for drops!</div>
            ) : (
              <div className="flex gap-2">
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                       placeholder="you@example.com"
                       className="flex-1 border border-zinc-200 rounded-lg px-3 py-2 text-sm"
                       data-testid="newsletter-email" />
                <button type="submit" disabled={busy}
                        className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#F74B64] text-white text-sm font-semibold disabled:opacity-50"
                        data-testid="newsletter-submit">
                  <Send size={12} /> {busy ? "…" : "Join"}
                </button>
              </div>
            )}
            <p className="text-xs text-zinc-400 mt-2">New flavour drops, delivery reminders, and member discounts.</p>
          </form>
        </div>

        <div>
          <div className="overline text-zinc-500 mb-4">Shop</div>
          <div className="flex flex-col gap-2 text-sm">
            <Link to="/shop" className="text-zinc-700 hover:text-zinc-900">All Flavours</Link>
            <Link to="/boxes" className="text-zinc-700 hover:text-zinc-900">Pick Ah Box</Link>
            <Link to="/build" className="text-zinc-700 hover:text-zinc-900">Build Yuh Own Box</Link>
            <Link to="/party" className="text-zinc-700 hover:text-zinc-900">Party Box</Link>
            <Link to="/track" className="text-zinc-700 hover:text-zinc-900 inline-flex items-center gap-1">
              <Search size={12} /> Track order
            </Link>
          </div>
        </div>

        <div>
          <div className="overline text-zinc-500 mb-4">Learn</div>
          <div className="flex flex-col gap-2 text-sm">
            <Link to="/blog" className="text-zinc-700 hover:text-zinc-900">Blog</Link>
            <a href="/#delivery" className="text-zinc-700 hover:text-zinc-900">Delivery Info</a>
          </div>
        </div>

        <div>
          <div className="overline text-zinc-500 mb-4">Connect</div>
          <div className="flex items-center gap-3 text-zinc-600">
            <a href="https://www.instagram.com/fruitblossomsja/" target="_blank" rel="noopener noreferrer"
               aria-label="Instagram" className="hover:text-[#F74B64] transition-colors"
               data-testid="footer-instagram">
              <Instagram size={18} />
            </a>
            <a href="https://www.facebook.com/fruitblossomsja/" target="_blank" rel="noopener noreferrer"
               aria-label="Facebook" className="hover:text-[#F74B64] transition-colors"
               data-testid="footer-facebook">
              <Facebook size={18} />
            </a>
            <a href="https://www.tiktok.com/@fruitblossoms" target="_blank" rel="noopener noreferrer"
               aria-label="TikTok" className="hover:text-[#F74B64] transition-colors"
               data-testid="footer-tiktok">
              <TikTokIcon size={18} />
            </a>
            <a href="mailto:fruitblossomsja@gmail.com" aria-label="Email"
               className="hover:text-[#F74B64] transition-colors" data-testid="footer-email-icon">
              <Mail size={18} />
            </a>
            <a href="tel:+18768495561" aria-label="Call us"
               className="hover:text-[#F74B64] transition-colors" data-testid="footer-phone-icon">
              <Phone size={18} />
            </a>
          </div>
          <div className="mt-4 space-y-1.5 text-xs text-zinc-600">
            <a href="tel:+18768495561" className="flex items-center gap-1.5 hover:text-zinc-900"
               data-testid="footer-phone">
              <Phone size={12} className="flex-shrink-0" style={{ color: "#F74B64" }} />
              <span>1&#8209;876&#8209;849&#8209;5561</span>
            </a>
            <a href="mailto:fruitblossomsja@gmail.com" className="flex items-center gap-1.5 hover:text-zinc-900 break-all"
               data-testid="footer-email">
              <Mail size={12} className="flex-shrink-0" style={{ color: "#F74B64" }} />
              <span>fruitblossomsja@gmail.com</span>
            </a>
          </div>
        </div>
      </div>
      {/* Jamaican flag accent strip */}
      <div className="h-1 w-full flex" aria-hidden="true">
        <div className="flex-1 bg-black" />
        <div className="flex-1 bg-[#FFD100]" />
        <div className="flex-1 bg-[#009A49]" />
      </div>
      <div className="border-t border-zinc-200 py-6 text-center text-xs text-zinc-500 space-y-1">
        <div>© {new Date().getFullYear()} Fruit Blossoms Jamaica. All rights reserved.</div>
        <div className="text-zinc-400">
          Powered by{" "}
          <a
            href="https://instagram.com/marketingwithnickm"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-zinc-500 hover:text-[#F74B64] transition-colors"
            data-testid="footer-powered-by"
          >
            MarketingWithNickM
          </a>
        </div>
      </div>
    </footer>
  );
}

/** TikTok logo — inline SVG since lucide-react doesn't ship one. */
function TikTokIcon({ size = 18 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size}
         fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.87a8.16 8.16 0 0 0 4.77 1.52V8a4.85 4.85 0 0 1-1.84-1.31Z"/>
    </svg>
  );
}
